import axios from 'axios';
import { handleAuthError } from '../utils/authUtils';

const API_BASE_URL = '/api';

export interface User {
  id: number;
  username: string;
  email: string;
  role: 'admin' | 'user';
  notification_preferences?: any;
  created_at: string;
  updated_at: string;
}

export interface CreateUserData {
  username: string;
  email: string;
  password: string;
  role: 'admin' | 'user';
  notification_preferences?: any;
}

export interface UpdateUserData {
  username?: string;
  email?: string;
  role?: 'admin' | 'user';
  notification_preferences?: any;
}

export interface UpdateUserPasswordData {
  password: string;
}

export interface UserNotificationPreference {
  id: number;
  user_id: number;
  notification_type: string;
  email_enabled: boolean;
  in_app_enabled: boolean;
  frequency: 'immediate' | 'daily' | 'weekly' | 'never';
  created_at: string;
  updated_at: string;
}

export interface UpdateUserNotificationPreferenceData {
  email_enabled?: boolean;
  in_app_enabled?: boolean;
  frequency?: 'immediate' | 'daily' | 'weekly' | 'never';
}

export interface NotificationType {
  type: string;
  name: string;
  description: string;
  default_frequency: string;
  severity: string;
}

export interface AuditLog {
  id: number;
  user_id: number;
  action: string;
  table_name: string;
  record_id: number | null;
  old_values: any;
  new_values: any;
  ip_address: string;
  user_agent: string;
  created_at: string;
  username: string;
  email: string;
}

export interface AuditLogFilters {
  record_id?: number;
  page?: number;
  limit?: number;
  user_id?: number;
  action?: string;
  table_name?: string;
  start_date?: string;
  end_date?: string;
}

export interface AuditLogResponse {
  logs: AuditLog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Configure axios to include auth token
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Use centralized auth error handler which handles both 401 and 403 with INVALID_TOKEN
    // and redirects with return URL
    handleAuthError(error);
    // Always reject to allow calling code to handle non-auth errors
    return Promise.reject(error);
  }
);

export const adminService = {
  // User Management
  async getAllUsers(): Promise<User[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/users`);
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.error?.message || 'Failed to fetch users');
    } catch (error: any) {
      if (error.response?.data?.error?.message) {
        throw new Error(error.response.data.error.message);
      }
      throw new Error('Failed to fetch users');
    }
  },

  async createUser(userData: CreateUserData): Promise<User> {
    try {
      const response = await axios.post(`${API_BASE_URL}/admin/users`, userData);
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.error?.message || 'Failed to create user');
    } catch (error: any) {
      if (error.response?.data?.error?.message) {
        throw new Error(error.response.data.error.message);
      }
      throw new Error('Failed to create user');
    }
  },

  async updateUser(userId: number, userData: UpdateUserData): Promise<User> {
    try {
      const response = await axios.put(`${API_BASE_URL}/admin/users/${userId}`, userData);
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.error?.message || 'Failed to update user');
    } catch (error: any) {
      if (error.response?.data?.error?.message) {
        throw new Error(error.response.data.error.message);
      }
      throw new Error('Failed to update user');
    }
  },

  async updateUserPassword(userId: number, data: UpdateUserPasswordData): Promise<void> {
    try {
      const response = await axios.put(`${API_BASE_URL}/admin/users/${userId}/password`, data);
      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'Failed to update password');
      }
    } catch (error: any) {
      if (error.response?.data?.error?.message) {
        throw new Error(error.response.data.error.message);
      }
      throw new Error('Failed to update password');
    }
  },

  async deleteUser(userId: number): Promise<void> {
    try {
      const response = await axios.delete(`${API_BASE_URL}/admin/users/${userId}`);
      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'Failed to delete user');
      }
    } catch (error: any) {
      if (error.response?.data?.error?.message) {
        throw new Error(error.response.data.error.message);
      }
      throw new Error('Failed to delete user');
    }
  },

  // Audit Logs
  async getAuditLogs(filters: AuditLogFilters = {}): Promise<AuditLogResponse> {
    try {
      const params = new URLSearchParams();

      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.user_id) params.append('user_id', filters.user_id.toString());
      if (filters.action) params.append('action', filters.action);
      if (filters.table_name) params.append('table_name', filters.table_name);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const response = await axios.get(`${API_BASE_URL}/admin/audit-logs?${params.toString()}`);
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.error?.message || 'Failed to fetch audit logs');
    } catch (error: any) {
      if (error.response?.data?.error?.message) {
        throw new Error(error.response.data.error.message);
      }
      throw new Error('Failed to fetch audit logs');
    }
  },

  // Notification Preferences Management
  async getUserNotificationPreferences(userId: number): Promise<UserNotificationPreference[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/users/${userId}/notifications`);
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.error?.message || 'Failed to fetch notification preferences');
    } catch (error: any) {
      if (error.response?.data?.error?.message) {
        throw new Error(error.response.data.error.message);
      }
      throw new Error('Failed to fetch notification preferences');
    }
  },

  async updateUserNotificationPreference(
    userId: number,
    notificationType: string,
    updates: UpdateUserNotificationPreferenceData
  ): Promise<UserNotificationPreference> {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/admin/users/${userId}/notifications/${notificationType}`,
        updates
      );
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.error?.message || 'Failed to update notification preference');
    } catch (error: any) {
      if (error.response?.data?.error?.message) {
        throw new Error(error.response.data.error.message);
      }
      throw new Error('Failed to update notification preference');
    }
  },

  async getNotificationTypes(): Promise<NotificationType[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/notification-types`);
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.error?.message || 'Failed to fetch notification types');
    } catch (error: any) {
      if (error.response?.data?.error?.message) {
        throw new Error(error.response.data.error.message);
      }
      throw new Error('Failed to fetch notification types');
    }
  },

  async sendTestNotificationEmail(
    userId: number,
    notificationType: string
  ): Promise<{ emailSent: boolean; recipient: string; notificationType: string }> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/admin/users/${userId}/notifications/${notificationType}/test`
      );
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.error?.message || 'Failed to send test email');
    } catch (error: any) {
      if (error.response?.data?.error?.message) {
        throw new Error(error.response.data.error.message);
      }
      throw new Error('Failed to send test email');
    }
  },
};
